import {
  GeneralLedgerAccount,
  ItemGeneralLedgerTransactionType,
  ItemType,
} from '../../../../clients/quipli-service/accounting/accounting';
import { QBOItemType } from '../../../../clients/quipli-service/qbo/qbo';
import { TaxCategory } from '../../../../clients/quipli-service/tax/tax';
import { Card } from '../../../../components/card/card';
import { Divider } from '../../../../components/divider/divider';
import { Toggle } from '../../../../components/form/toggle';
import { PrettyDescriptor } from '../../../../components/pretty-descriptor/pretty-descriptor';
import { SelectForm } from '../../../../components/select-form/select-form';
import SkeletonLoader from '../../../../components/skeleton-loader/skeleton-loader';
import { Typography } from '../../../../components/typography/typography';
import { absoluteSelectStyle } from '../../inventory-add-edit';
import '../../inventory-add-edit.scss';
import { ItemGeneralLedgerAccountInput } from '../../../accounting/item-general-ledger-account-input';
import { QboItemIncomeAccount } from '../../../qbo/qbo-item-income-account';

export const ProductTaxes = ({
  productId,
  chargeTax,
  setChargeTax,
  taxCategories,
  taxCategoryId,
  setTaxCategoryId,
  glAccounts,
  isInitiallyLoading,
  isSaving,
  type,
}: {
  productId: number;
  chargeTax: boolean;
  setChargeTax: (value: boolean) => void;
  taxCategories: TaxCategory[];
  taxCategoryId: string | undefined;
  setTaxCategoryId: (value: React.SetStateAction<string | undefined>) => void;
  glAccounts: GeneralLedgerAccount[];
  isInitiallyLoading: boolean;
  isSaving: boolean;
  type: 'Part' | 'Service';
}) => {
  const taxCategory = taxCategories.find((tc) => tc.id === taxCategoryId);
  const lowerType = type.toLowerCase();

  return (
    <Card className="card gap-4 w-auto min-w-[auto]">
      <div className="part-inventory-title-card">
        <Typography type="text-xl" weight="semi-bold">
          Taxes & Accounting
        </Typography>
        <Divider />
      </div>

      {isInitiallyLoading ? (
        <SkeletonLoader style={{ width: '--webkit-fill-available', height: '24vh' }} />
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="flex flex-col lg:flex-row lg:items-center mt-2">
              <PrettyDescriptor title="Apply Tax" description={`Enable to apply taxes to this ${lowerType}.`} />
            </div>

            <div className="flex flex-col lg:flex-row lg:items-center">
              <Toggle
                color="var(--quipli-yellow-300)"
                data-cy="part-location-toggle"
                checked={chargeTax}
                onClick={() => setChargeTax(!chargeTax)}
                disabled={isSaving}
              />
            </div>

            <div className="flex flex-col lg:flex-row lg:items-center">
              <PrettyDescriptor
                title="Tax Category"
                description={`Optional: Select a tax category if this ${lowerType} requires a specific tax rate based on predefined jurisdictions.`}
              />
            </div>
            <div className="flex flex-col lg:flex-row lg:items-center w-full lg:w-3/4">
              <SelectForm
                value={{
                  value: taxCategoryId,
                  label: taxCategory?.name ?? 'Select Tax Category',
                }}
                styles={absoluteSelectStyle}
                options={taxCategories.map((tc) => ({ label: tc.name, value: tc.id }))}
                onChange={(s) => setTaxCategoryId(s?.value)}
                isClearable={taxCategory !== undefined}
                isDisabled={isSaving}
                className="select-tax-category"
              />
            </div>
            <div className="flex flex-col w-full col-span-full">
              <Divider />
            </div>
            <div className="flex flex-col w-full ">
              <PrettyDescriptor
                title="Account Mapping"
                description={`Specify where revenue from this ${lowerType}'s sales will be recorded for accounting.`}
              />
            </div>
            <div className="flex flex-col w-full lg:w-3/4 gap-4">
              <div className="w-full">
                <PrettyDescriptor
                  title="Income GL Account"
                  tooltipText={`Select the General Ledger account to record sales revenue for this ${lowerType}.`}
                />
                <ItemGeneralLedgerAccountInput
                  itemType={ItemType.Product}
                  itemId={productId.toString()}
                  transactionType={ItemGeneralLedgerTransactionType.Sale}
                  accounts={glAccounts}
                />
              </div>
              <div className="w-full">
                <PrettyDescriptor
                  title="Expense GL Account"
                  tooltipText={`Select the General Ledger account to record sales revenue for this ${lowerType}.`}
                />
                <ItemGeneralLedgerAccountInput
                  itemType={ItemType.Product}
                  itemId={productId.toString()}
                  transactionType={ItemGeneralLedgerTransactionType.Expense}
                  accounts={glAccounts}
                />
              </div>
              <div className="w-full">
                <PrettyDescriptor
                  title="QuickBooks Online Sales Account"
                  tooltipText={`Select the QuickBooks Online account to record sales revenue for this ${lowerType}.`}
                />
                <QboItemIncomeAccount
                  itemType={QBOItemType.Product}
                  itemId={productId.toString()}
                  transactionType={ItemGeneralLedgerTransactionType.Sale}
                />
              </div>

              <div className="w-full">
                <PrettyDescriptor
                  title="QuickBooks Online Expense Account"
                  tooltipText={`Select the QuickBooks Online account to record expenses for this ${lowerType}.`}
                />
                <QboItemIncomeAccount
                  itemType={QBOItemType.Product}
                  itemId={productId.toString()}
                  transactionType={ItemGeneralLedgerTransactionType.Expense}
                />
              </div>
            </div>
            <div className="flex flex-col w-full col-span-full">
              <Divider />
            </div>
          </div>
        </>
      )}
    </Card>
  );
};
